import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit, FiEye, FiFilter, FiCalendar, FiColumns, FiList, FiFileText } from 'react-icons/fi';
import visitService from '../../services/visitService';
import stageService from '../../services/stageService';
import api from '../../services/api';
import { selectReviewerTierStages } from '../../utils/crfReviewerStages';
import { VISIT_STATUS, VISIT_TYPES, STAGE_WORKFLOW, formatVisitId, dispatchNotificationsRefresh } from '../../utils/constants';
import {
  formatDateDDMMMYYYY,
  formatIsoDateCalendarDDMMMYYYY,
  formatOffsetForDisplay,
  formatPerformedOnUtcToLocal,
  apiDateTimeToIsoDateLocal,
  todayIsoDateLocal,
  getActionItemClosingDateBounds,
  clampIsoDateToMinMax,
} from '../../utils/dateFormat';
import { formatActionItemStatusForDisplay, normalizeActionItemStatusSelectValue } from '../../utils/actionItemUi';
import auditService from '../../services/auditService';
import { common as commonMessages, visits as visitMessages, actionItems as actionItemMessages } from '../../utils/validationMessages';
import { useProject } from '../../context/ProjectContext';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import VisitCalendar from './VisitCalendar';
import ScheduleVisitModal from './ScheduleVisitModal';
import ReportReviewModal from './ReportReviewModal';
import VisitActionsModal from './VisitActionsModal';
import ConfirmPIModal from './ConfirmPIModal';
import GenerateVisitLetterModal from './GenerateVisitLetterModal';
import GenerateFollowUpLetterModal from './GenerateFollowUpLetterModal';
import ConfirmFollowUpPIModal from './ConfirmFollowUpPIModal';
import ExecuteVisitModal from './ExecuteVisitModal';
import CreateActionItemModal from '../ActionItems/CreateActionItemModal';
import { Modal } from '../Common/Modal';
import ElectronicSign from '../Common/ElectronicSign';
import ActionItemReplyThread from '../Common/ActionItemReplyThread';
import {
  formatReplyRemarkPlainForAuditDisplay,
  getClosureRemarkFromReplyRemark,
  stripClosureRemarkFromReplyRemark,
} from '../../utils/actionItemReplyThread';
import Pagination from '../Common/Pagination';
import CRFForm from '../CRF/CRFForm';
import '../Common/DataGrid.css';
import './VisitManagement.css';

/** Map dashboard / URL ?stage= value to internal activeStage id. */
function parseVisitsUrlStageParam(param) {
  if (param == null) return null;
  const p = String(param).trim();
  if (!p) return null;
  const lower = p.toLowerCase();
  if (lower === 'schedule') return 'schedule';
  if (lower === 'ongoing' || lower === 'inprogress' || lower === 'in_progress' || p === 'onGoing') return 'onGoing';
  if (lower === 'closed' || lower === 'completed') return 'closed';
  const n = parseInt(p, 10);
  if (!Number.isNaN(n)) return String(n);
  return null;
}

/** Same mapping as in-component stageCodeToId (for effects that run before inner function init). */
function stageCodeToActiveStageId(code) {
  if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
  if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
  if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
  if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
  return null;
}

/**
 * Match search text within a single column only. Joining all columns with spaces caused false
 * positives (e.g. "Site Selection Visit 1" matching visit type + start date "11-Apr-2026").
 */
/** Audit cell display (ISO dates → dd-mmm-yyyy) — same idea as CRFForm field audit. */
function formatIssueAuditValueForDisplay(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(raw);
  if (!m) return raw;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return raw;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return raw;
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return raw;
  return formatDateDDMMMYYYY(dt);
}

function visitRowMatchesSearch(v, qRaw) {
  const q = (qRaw || '').trim().toLowerCase();
  if (!q) return true;
  const visitId = formatVisitId(v);
  const visitType = (VISIT_TYPES[v.visitType] || v.visitType || '').toString();
  const plannedStart = v.plannedStartDate ? formatDateDDMMMYYYY(v.plannedStartDate) : '';
  const plannedEnd = v.plannedEndDate ? formatDateDDMMMYYYY(v.plannedEndDate) : '';
  const actualStart = v.actualStartDate ? formatDateDDMMMYYYY(v.actualStartDate) : '';
  const actualEnd = v.actualEndDate ? formatDateDDMMMYYYY(v.actualEndDate) : '';
  const status = (v.visitStatus ?? v.VisitStatus ?? '').toString();
  const site = (v.siteName ?? v.SiteName ?? '').toString();
  const siteCode = (v.vSiteCode ?? v.siteCode ?? v.SiteCode ?? '').toString();
  const fields = [visitId, visitType, plannedStart, plannedEnd, actualStart, actualEnd, status, site, siteCode];
  return fields.some((field) => String(field || '').toLowerCase().includes(q));
}

const VisitManagement = () => {
  const { user } = useContext(AuthContext);
  const { nRecordedBy: sessionRecordedByMappingId } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentProject: contextProject, updateProject: setCurrentProject, projectInfo } = useProject();
  const currentProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStage, setActiveStage] = useState('schedule');
  const [viewMode, setViewMode] = useState('calendar'); // 'panel' or 'calendar' - default to calendar for schedule
  const [filters, setFilters] = useState({
    site: 'all',
    cra: 'all',
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedVisitForReview, setSelectedVisitForReview] = useState(null);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
  const [selectedVisitForActions, setSelectedVisitForActions] = useState(null);
  const [isConfirmPIModalOpen, setIsConfirmPIModalOpen] = useState(false);
  const [visitForConfirmPI, setVisitForConfirmPI] = useState(null);
  const [isGenerateLetterModalOpen, setIsGenerateLetterModalOpen] = useState(false);
  const [visitForLetter, setVisitForLetter] = useState(null);
  const [isGenerateFollowUpLetterModalOpen, setIsGenerateFollowUpLetterModalOpen] = useState(false);
  const [visitForFollowUpLetter, setVisitForFollowUpLetter] = useState(null);
  const [isConfirmFollowUpPIModalOpen, setIsConfirmFollowUpPIModalOpen] = useState(false);
  const [visitForFollowUpConfirmPI, setVisitForFollowUpConfirmPI] = useState(null);
  const [isExecuteVisitModalOpen, setIsExecuteVisitModalOpen] = useState(false);
  const [visitForExecute, setVisitForExecute] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportTemplateId, setReportTemplateId] = useState(null);
  const [reportVisit, setReportVisit] = useState(null);
  const [visitBeingEdited, setVisitBeingEdited] = useState(null);
  const [reviewTab, setReviewTab] = useState('pending'); // 'pending' or 'actions' for review stages
  const [actionItems, setActionItems] = useState([]);
  const [actionItemClosingId, setActionItemClosingId] = useState(null);
  const [actionItemReopeningId, setActionItemReopeningId] = useState(null);
  const [issueWorkflowModalOpen, setIssueWorkflowModalOpen] = useState(false);
  const [issueWorkflowMode, setIssueWorkflowMode] = useState('reply'); // reply | view
  const [selectedIssueItem, setSelectedIssueItem] = useState(null);
  /** Full persisted Description of Action (vReplyRemark) from server. */
  const [issueReplyRemark, setIssueReplyRemark] = useState('');
  /** New reply body only (server stamps user + datetime on append). */
  const [issueReplyDraft, setIssueReplyDraft] = useState('');
  const [issueReplyHistoryEntries, setIssueReplyHistoryEntries] = useState([]);
  /** View Issue (Resolved workflow): mirrors CRFForm Issue Review — Status Open/Close drives reopen intent + description enable. */
  const [viewIssueReopenIntent, setViewIssueReopenIntent] = useState('Closed');
  /** yyyy-MM-dd when closing from Visit Issue Review (creator path). */
  const [viewIssueClosingDate, setViewIssueClosingDate] = useState('');
  const viewIssueReplyOriginalRef = useRef('');
  const [issueViewAuditOpen, setIssueViewAuditOpen] = useState(false);
  const [issueViewAuditLoading, setIssueViewAuditLoading] = useState(false);
  const [issueViewAuditError, setIssueViewAuditError] = useState(null);
  const [issueViewAuditTrail, setIssueViewAuditTrail] = useState([]);
  const [issueViewAuditField, setIssueViewAuditField] = useState('');
  const [issueViewAuditActionItemId, setIssueViewAuditActionItemId] = useState(null);
  /** Inline validation under Action Item Description (reply modal); not toaster */
  const [issueReplyRemarkFieldError, setIssueReplyRemarkFieldError] = useState('');
  /** null | 'update' (remark only) | 'finalize' (submit to creator) */
  const [issueReplyBusyKind, setIssueReplyBusyKind] = useState(null);
  const [showCloseIssueRemarkModal, setShowCloseIssueRemarkModal] = useState(false);
  const closeIssueRemarkRef = useRef('');
  const [showReopenIssueRemarkModal, setShowReopenIssueRemarkModal] = useState(false);
  const reopenIssueRemarkRef = useRef('');
  const [workflowStagesFromApi, setWorkflowStagesFromApi] = useState([]); // dynamic stages from GET /api/stage (profile-filtered)
  /** Reviewer-tier labels from stt.StageMst for current project (all stages); drives CRF form header "… By:" labels. */
  const [crfReviewerStageLabels, setCrfReviewerStageLabels] = useState([]);
  const [stageCountsByCode, setStageCountsByCode] = useState({}); // stageCode -> count from GET /api/visit/stage-counts
  const [stagesLoaded, setStagesLoaded] = useState(false); // true after first getStages response so we can show "no stages" vs loading
  const [isCreateActionItemModalOpen, setIsCreateActionItemModalOpen] = useState(false);
  const [issueDetailsModalOpen, setIssueDetailsModalOpen] = useState(false);
  const [selectedIssueForDetails, setSelectedIssueForDetails] = useState(null);
  const [sites, setSites] = useState([]);
  const [cras, setCras] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [visitsWithCRFDraft, setVisitsWithCRFDraft] = useState(() => new Set());
  const [loadingCRFDraftStatus, setLoadingCRFDraftStatus] = useState(false);
  const [profilePermissions, setProfilePermissions] = useState({});
  const [visitSearch, setVisitSearch] = useState('');
  const [visitPage, setVisitPage] = useState(1);
  const [visitPageSize, setVisitPageSize] = useState(10);
  const [actionItemPage, setActionItemPage] = useState(1);
  const [actionItemPageSize, setActionItemPageSize] = useState(10);
  const [actionItemSearch, setActionItemSearch] = useState('');
  const [toast, setToast] = useState(null);
  /** Supersedes in-flight GET /visit when stage/project changes (e.g. ?stage= runs after first fetch effect). */
  const visitsFetchGenerationRef = useRef(0);

  // Profile activity permissions: iScheduleModify, iVisitActivity, iStartReport, iApproveSendback; IsViewOnly hides all transactional actions
  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) setProfilePermissions(res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentProject]);

  const isViewOnly = profilePermissions.isViewOnly === 1 || profilePermissions.IsViewOnly === 1;
  const canSchedule = !isViewOnly && (profilePermissions.iScheduleModify === 1 || profilePermissions.iScheduleModify === '1');
  const canVisitActivity = !isViewOnly && (profilePermissions.iVisitActivity === 1 || profilePermissions.iVisitActivity === '1');
  const canStartReport = !isViewOnly && (profilePermissions.iStartReport === 1 || profilePermissions.iStartReport === '1');
  const canApproveReject = !isViewOnly && (profilePermissions.iApproveSendback === 1 || profilePermissions.iApproveSendback === '1');
  const canRaiseDCFPerm = !isViewOnly && (profilePermissions.canRaiseDCF === 1 || profilePermissions.CanRaiseDCF === 1);
  /** Dual-visibility "peek" read-only must not lock PM / first-tier reviewers who still need to edit answers and Submit from a review tab. */
  const visitReportDualVisibilityPeekReadOnlyExempt = (() => {
    const code = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
    if (['PM', 'FRM'].includes(code)) return true;
    if (canRaiseDCFPerm && code !== 'SRM') return true;
    return false;
  })();
  const currentUserId = Number(user?.iuid ?? user?.Iuid ?? user?.userId ?? user?.UserId ?? user?.id ?? 0) || 0;

  /** Profile stage mapping from GET /api/stage: user may open Completed tab only if this is true. */
  const hasCompletedStageRights = useMemo(
    () => workflowStagesFromApi.some(
      (s) => Number(s.stageCode ?? s.StageCode) === STAGE_WORKFLOW.COMPLETED,
    ),
    [workflowStagesFromApi],
  );

  /** Resolved + session UserRoleProjectSiteMapping id matches createdBy + profile match for Close in View modal. */
  const canShowIssueCloseButton = (item) => {
    if (!item) return false;
    if ((item.status || '').toString().toLowerCase() !== 'resolved') return false;
    const cur = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
    const raised = (item.createdByProfileCode ?? item.CreatedByProfileCode ?? '').toString().trim().toUpperCase();
    if (!cur) return false;
    if (!raised) return false;
    return cur === raised;
  };

  /** Resolved or Closed: any user may use Issue Review reopen (API does not restrict reopen). */
  const canShowIssueReopenButton = (item) => {
    if (!item) return false;
    const st = (item.status || '').toString().trim().toLowerCase();
    return st === 'resolved' || st === 'closed';
  };

  useEffect(() => {
    if (!issueWorkflowModalOpen || !selectedIssueItem) {
      setIssueReplyHistoryEntries([]);
      return;
    }
    const aid = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
    if (!aid) {
      setIssueReplyHistoryEntries([]);
      return;
    }

    let cancelled = false;
    const pid = projectInfo?.projectNo ?? projectInfo?.ProjectNo ?? projectInfo?.projectId ?? projectInfo?.ProjectId;
    const pcode = (typeof contextProject === 'string' && contextProject && contextProject !== 'all')
      ? contextProject
      : (projectInfo?.projectCode ?? projectInfo?.ProjectCode);

    auditService
      .getAuditHdrTrail('CRFActionItem', String(aid), 200, {
        projectId: pid != null && pid !== '' && Number(pid) !== 0 ? pid : undefined,
        projectCode: pid != null && pid !== '' && Number(pid) !== 0 ? undefined : pcode,
      })
      .then((data) => {
        if (cancelled) return;
        const trail = Array.isArray(data) ? data : [];
        const entries = trail.flatMap((hdr, hi) => {
          const details = hdr.details || hdr.Details || [];
          const recordedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
          const offset = hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset || '';
          const valueRows = details.filter((d) => (d.vFieldName || d.VFieldName || '').toLowerCase() === 'vreplyremark');
          return valueRows.map((dtl, di) => {
            const sign = (dtl.vRecordedSign ?? dtl.VRecordedSign ?? hdr.performedBy ?? hdr.vRecordedSign ?? '—').toString().trim() || '—';
            const rawNew = dtl.vNewValue ?? dtl.VNewValue;
            const body = formatReplyRemarkPlainForAuditDisplay(rawNew).toString().trim();
            const at = formatPerformedOnUtcToLocal(recordedOn, offset);
            return {
              key: `${hdr.nAuditHdrNo ?? hi}-${di}`,
              metaText: `${sign} ${at && at !== '—' ? at : ''}`.trim(),
              body,
              // Keep Issue Review Description of Action audit-style entries left-aligned.
              isMine: false,
              recordedOn: recordedOn ? new Date(recordedOn).getTime() : 0,
              auditDtlNo: Number(dtl.nAuditDtlNo ?? dtl.NAuditDtlNo ?? 0),
            };
          });
        })
          .filter((x) => x.body !== '' && x.body.toLowerCase() !== 'null')
          .sort((a, b) => {
            const t = (a.recordedOn || 0) - (b.recordedOn || 0);
            if (t !== 0) return t;
            return (a.auditDtlNo || 0) - (b.auditDtlNo || 0);
          });
        setIssueReplyHistoryEntries(entries);
      })
      .catch(() => {
        if (!cancelled) setIssueReplyHistoryEntries([]);
      });

    return () => { cancelled = true; };
  }, [issueWorkflowModalOpen, selectedIssueItem, contextProject, projectInfo, user]);

  useEffect(() => {
    if (!issueViewAuditOpen || !issueViewAuditActionItemId) {
      setIssueViewAuditTrail([]);
      setIssueViewAuditError(null);
      return;
    }
    let cancelled = false;
    setIssueViewAuditLoading(true);
    setIssueViewAuditError(null);
    const pid = projectInfo?.projectNo ?? projectInfo?.ProjectNo ?? projectInfo?.projectId ?? projectInfo?.ProjectId;
    const pcode = (typeof contextProject === 'string' && contextProject && contextProject !== 'all')
      ? contextProject
      : (projectInfo?.projectCode ?? projectInfo?.ProjectCode);
    auditService
      .getAuditHdrTrail('CRFActionItem', String(issueViewAuditActionItemId), 100, {
        projectId: pid != null && pid !== '' && Number(pid) !== 0 ? pid : undefined,
        projectCode: pid != null && pid !== '' && Number(pid) !== 0 ? undefined : pcode,
      })
      .then((data) => {
        if (!cancelled) setIssueViewAuditTrail(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setIssueViewAuditError(err?.response?.data?.error || err?.message || 'Failed to load audit trail');
      })
      .finally(() => {
        if (!cancelled) setIssueViewAuditLoading(false);
      });
    return () => { cancelled = true; };
  }, [issueViewAuditOpen, issueViewAuditActionItemId, contextProject, projectInfo]);

  useEffect(() => {
    const loadFilters = async () => {
      setLoadingFilters(true);
      try {
        // Fetch both sites and CRAs in parallel from database
        await Promise.all([
          fetchSites(),
          fetchCRAs()
        ]);
      } catch (err) {
        console.error('Error loading filters:', err);
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]); // Refetch sites when project changes

  useEffect(() => {
    fetchVisits();
  }, [filters, currentProject, activeStage]);

  useEffect(() => {
    setVisitPage(1);
    setActionItemPage(1);
    setActionItemSearch('');
  }, [activeStage, reviewTab]);

  // Fetch dynamic stages from API (profile-filtered by backend); refetch when project changes
  useEffect(() => {
    let cancelled = false;
    setStagesLoaded(false);
    stageService.getStages(currentProject || undefined)
      .then((list) => {
        if (!cancelled && Array.isArray(list)) {
          setWorkflowStagesFromApi(list);
          setStagesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setStagesLoaded(true);
      });
    return () => { cancelled = true; };
  }, [currentProject]);

  // Full StageMst for project (Site Visit & Report Management): CRF headers use same reviewer labels for all forms.
  useEffect(() => {
    let cancelled = false;
    const pc = currentProject == null || currentProject === 'all'
      ? ''
      : (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode ?? ''));
    if (!pc) {
      setCrfReviewerStageLabels([]);
      return undefined;
    }
    stageService.getAllStagesForProject(pc)
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const tiers = selectReviewerTierStages(list);
        setCrfReviewerStageLabels(tiers.map((t) => ({ label: t.label, stageCode: t.stageCode })));
      })
      .catch(() => {
        if (!cancelled) setCrfReviewerStageLabels([]);
      });
    return () => { cancelled = true; };
  }, [currentProject]);

  // Stage counts (project + profile): refetch on project change and after visit mutations (schedule, execute, etc.)
  const refetchStageCounts = useCallback(async () => {
    const projectCodeForApi = currentProject == null || currentProject === 'all'
      ? undefined
      : (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode));
    try {
      const rows = await stageService.getVisitStageCounts(projectCodeForApi || undefined);
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const code = r.stageCode ?? r.StageCode;
        const count = r.count ?? r.Count ?? 0;
        if (code != null) map[Number(code)] = Number(count) || 0;
      });
      setStageCountsByCode(map);
    } catch {
      setStageCountsByCode({});
    }
  }, [currentProject]);

  useEffect(() => {
    void refetchStageCounts();
  }, [refetchStageCounts]);

  // Fetch CRF action items by stageCode + projectCode (dynamic stages; no static stage names)
  useEffect(() => {
    const code = activeStage === 'onGoing' ? STAGE_WORKFLOW.IN_PROGRESS : (activeStage === 'schedule' || activeStage === 'closed' ? null : parseInt(activeStage, 10));
    if (code == null || isNaN(code)) {
      setActionItems([]);
      return;
    }
    refetchActionItems();
  }, [activeStage, filters, currentProject, sites, user?.profileCode, user?.vUserTypeCode, user?.role, sessionRecordedByMappingId]);

  // Load CRF draft status for In Progress visits so "Continue" shows instead of "Start Report"
  useEffect(() => {
    if (activeStage !== 'onGoing') {
      setLoadingCRFDraftStatus(false);
      return;
    }
    const filtered = (Array.isArray(visits) ? visits : []).filter((v) => {
      if (!v) return false;
      if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
      if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
      return true;
    });
    const inProgressLabel = (() => {
      const code = STAGE_WORKFLOW.IN_PROGRESS;
      const s = workflowStagesFromApi.find((x) => (x.stageCode ?? x.StageCode) === code);
      return s ? (s.label ?? s.Label ?? s.vDisplayName ?? s.VDisplayName ?? '') : '';
    })();
    const onGoingVisits = filtered.filter((v) => {
      const crf = v.crfStage ?? v.CrfStage ?? '';
      const hasOpenDCF = v.hasOpenDCF ?? v.HasOpenDCF;
      if (hasOpenDCF) return true;
      if (crf !== '' && crf !== inProgressLabel) return false;
      return (v.visitStatus ?? v.VisitStatus) === VISIT_STATUS.ONGOING || (inProgressLabel && crf === inProgressLabel);
    });
    if (onGoingVisits.length === 0) {
      setLoadingCRFDraftStatus(false);
      return;
    }

    setLoadingCRFDraftStatus(true);
    let cancelled = false;
    const checkDrafts = async () => {
      const draftVisitIds = new Set();
      await Promise.all(
        onGoingVisits.map(async (visit) => {
          const vid = visit?.scheduledVisitID ?? visit?.scheduledVisitId ?? visit?.ScheduledVisitID;
          if (vid == null) return;
          if (visit.hasOpenDCF ?? visit.HasOpenDCF) {
            draftVisitIds.add(Number(vid));
            return;
          }
          try {
            const templateRes = await visitService.getAssignedCRFTemplate(vid);
            const templateId = templateRes?.templateId ?? templateRes?.TemplateId;
            if (!templateId) return;
            const hasRes = await api.get(`/crf/page/has-draft/${vid}/template/${templateId}`);
            const hasDraft = hasRes.data?.hasDraft ?? hasRes.data?.HasDraft;
            if (hasDraft) draftVisitIds.add(Number(vid));
          } catch (_) { /* ignore */ }
        })
      );
      if (!cancelled) {
        setVisitsWithCRFDraft((prev) => new Set([...prev, ...draftVisitIds]));
        setLoadingCRFDraftStatus(false);
      }
    };
    checkDrafts();
    return () => {
      cancelled = true;
      setLoadingCRFDraftStatus(false);
    };
  }, [activeStage, visits, filters, workflowStagesFromApi]);

  const fetchSites = async () => {
    try {
      // Fetch sites filtered by current project if available
      const url = currentProject && currentProject !== 'all'
        ? `/sites?project=${currentProject}`
        : '/sites';
      const response = await api.get(url);

      if (!response.data) {
        console.warn('No sites data received from API');
        setSites([]);
        return;
      }

      // Map sites: use SiteCode for filter value; display as vSiteCode + vSiteDescription
      const sitesList = (response.data || [])
        .map(site => {
          const siteCode = (site.siteCode ?? site.vSiteCode ?? site.sitedescription ?? '').toString().trim();
          const siteDescription = (site.sitedescription ?? site.siteDescription ?? site.SiteDescription ?? '').toString().trim();
          const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription;
          return {
            siteID: site.siteID,
            siteCode,
            siteName: site.sitedescription || site.siteCode || '',
            siteDescription,
            siteDisplay,
            projectCode: site.projectCode || null,
            piName: site.piName ?? site.investigator ?? '',
          };
        })
        .filter(site => site.siteCode) // Filter out empty site codes
        .sort((a, b) => a.siteCode.localeCompare(b.siteCode));

      setSites(sitesList);
      console.log(`Loaded ${sitesList.length} sites from database`);
    } catch (err) {
      console.error('Error fetching sites from database:', err);
      setError(err.response?.data?.message || 'Failed to load sites from database');
      setSites([]);
    }
  };

  const getVisitSiteDisplay = (visit, sitesList) => {
    if (!visit) return '—';
    const code = visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '';
    let name = visit.siteName ?? visit.SiteName ?? '';
    if (!name) {
      const siteId = visit.siteID ?? visit.siteId ?? visit.SiteID;
      if (siteId != null && sitesList && sitesList.length > 0) {
        const site = sitesList.find((s) => String(s.siteID ?? s.siteId) === String(siteId));
        name = site?.siteDisplay ?? site?.siteName ?? site?.sitedescription ?? '';
      }
    }
    const codeStr = (code || '').toString().trim();
    const nameStr = (name || '').toString().trim();
    const combined = [codeStr, nameStr].filter(Boolean).join(' - ');
    return combined || codeStr || nameStr || '—';
  };

  /** Site label for issue rows: prefer staged visit row, else API snapshot (site when visit is not in current stage list). */
  const getActionItemSiteDisplay = (item, visit, sitesList) => {
    const fromVisit = getVisitSiteDisplay(visit, sitesList);
    if (fromVisit && fromVisit !== '—') return fromVisit;
    const code = (item.siteCode ?? item.SiteCode ?? '').toString().trim();
    const desc = (item.siteDescription ?? item.SiteDescription ?? '').toString().trim();
    const combined = [code, desc].filter(Boolean).join(' - ');
    if (combined) return combined;
    const sid = item.siteId ?? item.siteID ?? item.SiteID;
    if (sid != null && sitesList && sitesList.length > 0) {
      const site = sitesList.find((s) => String(s.siteID ?? s.siteId) === String(sid));
      const d = site?.siteDisplay ?? site?.siteName ?? '';
      if (d) return d;
    }
    return '—';
  };

  const getActionItemVisitTypeLabel = (item, visit) => {
    const code = (visit?.visitType ?? item.visitType ?? item.VisitType ?? '').toString().trim();
    if (!code) return '-';
    return VISIT_TYPES[code] || code;
  };

  /** Visit ID for issue row: same as visits grid (DisplayVisitCode or VisitType+ScheduledVisitID). */
  const getActionItemVisitIdDisplay = (item, visit) => {
    if (visit) return formatVisitId(visit);
    const dc = (item.displayVisitCode ?? item.DisplayVisitCode ?? '').toString().trim();
    if (dc) return dc;
    const vid = item.scheduledVisitID ?? item.ScheduledVisitID;
    const vt = (item.visitType ?? item.VisitType ?? 'SMV').toString().trim();
    if (vid != null && String(vid) !== '') return `${vt}${vid}`;
    return '—';
  };

  /** Case-insensitive match on any issue grid column (and common raw API fields). */
  const actionItemRowMatchesSearch = (item, visit, qRaw) => {
    const q = (qRaw || '').trim().toLowerCase();
    if (!q) return true;
    const refNo = String(item.actionItemNumber ?? item.vActionItemNumber ?? item.nCRFActionItemNo ?? '').toLowerCase();
    const visitIdDisp = String(getActionItemVisitIdDisplay(item, visit) ?? '').toLowerCase();
    const siteDisp = String(getActionItemSiteDisplay(item, visit, sites) ?? '').toLowerCase();
    const visitType = String(getActionItemVisitTypeLabel(item, visit) ?? '').toLowerCase();
    const issueOn = String(item.actionItemOn ?? '').toLowerCase();
    const finding = String(item.findingDescription ?? '').toLowerCase();
    const assigned = String(
      item.assignedToName || item.assignedToProfileCode || 'CRA',
    ).toLowerCase();
    const due = item.dueDate ? formatDateDDMMMYYYY(item.dueDate).toLowerCase() : '';
    const priority = String(item.priorityDescription ?? item.severity ?? '').toLowerCase();
    const status = String(item.status || 'Open').toLowerCase();
    const visitIdRaw = String(item.scheduledVisitID ?? item.ScheduledVisitID ?? '').toLowerCase();
    const displayCode = String(item.displayVisitCode ?? item.DisplayVisitCode ?? '').toLowerCase();
    const fields = [
      refNo,
      visitIdDisp,
      siteDisp,
      visitType,
      issueOn,
      finding,
      assigned,
      due,
      priority,
      status,
      visitIdRaw,
      displayCode,
    ];
    return fields.some((f) => f.includes(q));
  };

  /** Resolve PI (Investigator) from visit or from saved site when visit has no PI. */
  const getVisitPI = (visit, sitesList) => {
    if (!visit) return '';
    const fromVisit = visit.piName ?? visit.PIName ?? visit.pIName ?? '';
    if (fromVisit && String(fromVisit).trim()) return fromVisit.trim();
    const siteId = visit.siteID ?? visit.siteId ?? visit.SiteID;
    if (siteId != null && sitesList && sitesList.length > 0) {
      const site = sitesList.find((s) => String(s.siteID ?? s.siteId) === String(siteId));
      const fromSite = site?.piName ?? site?.investigator ?? '';
      if (fromSite && String(fromSite).trim()) return fromSite.trim();
    }
    return '';
  };

  const getVisitCraFilterId = (visit) => {
    if (!visit) return '';
    const craId = visit.cra_UserID ?? visit.CRA_UserID ?? visit.craID ?? visit.Cra_UserID ?? visit.CraUserID;
    return craId != null && String(craId).trim() !== '' ? String(craId) : '';
  };

  const fetchCRAs = async () => {
    try {
      // Visit filter only: session study-design lane (Blinded vs Unblinded). Assign/Schedule keep /users/cras.
      const response = await api.get(
        '/users/cras-for-visit-filter',
        currentProject ? { params: { project: currentProject } } : {}
      );

      if (!response.data) {
        console.warn('No CRAs data received from API');
        setCras([]);
        return;
      }

      // Way2: filter value = mapping PK (optionId); lane-matched CRA profiles only
      const raw = response.data || [];
      const seen = new Set();
      const crasList = raw
        .map((cra) => {
          const mappingId = String(cra.optionId || cra.mappingId || cra.id);
          return { id: mappingId, name: cra.name || '' };
        })
        .filter((cra) => cra.name && !seen.has(cra.id) && seen.add(cra.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      setCras(crasList);
      console.log(`Loaded ${crasList.length} CRAs from database`);
    } catch (err) {
      console.error('Error fetching CRAs from database:', err);
      setError(err.response?.data?.message || 'Failed to load CRAs from database');
      setCras([]);
    }
  };

  const refetchActionItems = async () => {
    // Dynamic stage: use stageCode (101, 201, 9999 fixed; between 201 and 9999 study-specific). No static stage names.
    const stageCode = activeStage === 'onGoing' ? STAGE_WORKFLOW.IN_PROGRESS : (activeStage === 'schedule' || activeStage === 'closed' ? null : parseInt(activeStage, 10));
    if (stageCode == null || isNaN(stageCode) || !currentProject) {
      setActionItems([]);
      return;
    }
    const params = { stageCode, projectCode: currentProject };
    const viewerProfileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
    if (viewerProfileCode) params.assignedToProfileCode = viewerProfileCode;
    if (filters.site && filters.site !== 'all') {
      const site = sites.find((s) => (s.siteCode ?? s.siteName) === filters.site);
      if (site && (site.siteID ?? site.siteId) != null) params.siteId = site.siteID ?? site.siteId;
    }
    try {
      const res = await api.get('/action-items', { params });
      const list = Array.isArray(res.data) ? res.data : (res.data?.data && Array.isArray(res.data.data) ? res.data.data : []);
      setActionItems(list);
    } catch (err) {
      console.error('Error fetching issues:', err);
      setActionItems([]);
    }
  };

  const openReplyModal = (item) => {
    setSelectedIssueItem(item);
    setIssueWorkflowMode('reply');
    const reply = (item?.replyRemark || '').toString();
    viewIssueReplyOriginalRef.current = reply;
    setIssueReplyRemark(reply);
    setIssueReplyDraft('');
    setViewIssueReopenIntent('Closed');
    setViewIssueClosingDate('');
    setIssueReplyRemarkFieldError('');
    setIssueWorkflowModalOpen(true);
  };

  const openViewModal = (item) => {
    setSelectedIssueItem(item);
    setIssueWorkflowMode('view');
    const reply = (item?.replyRemark || '').toString();
    viewIssueReplyOriginalRef.current = reply;
    setIssueReplyRemark(reply);
    setIssueReplyDraft('');
    setViewIssueReopenIntent('Closed');
    const st = (item?.status || '').toString().trim().toLowerCase();
    if (st === 'resolved' && canShowIssueCloseButton(item)) {
      const b = getActionItemClosingDateBounds(item?.dateOfIssue ?? item?.DateOfIssue);
      setViewIssueClosingDate(clampIsoDateToMinMax(todayIsoDateLocal(), b.minIso, b.maxIso));
    } else {
      setViewIssueClosingDate('');
    }
    setIssueReplyRemarkFieldError('');
    setIssueWorkflowModalOpen(true);
  };

  const handleUpdateIssueRemarkOnly = async () => {
    const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
    if (!id) return;
    const draft = issueReplyDraft.trim();
    if (!draft) {
      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
      return;
    }
    setIssueReplyRemarkFieldError('');
    setIssueReplyBusyKind('update');
    try {
      const body = { replyRemark: draft, submitToCreator: false };
      await api.post(`/action-items/${id}/update`, body);
      setToast({ type: 'success', message: actionItemMessages.remarkSavedSuccess });
      await refetchActionItems();
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyDraft('');
      setIssueReplyRemarkFieldError('');
      setViewIssueReopenIntent('Closed');
      setViewIssueClosingDate('');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update remark.';
      setToast({ type: 'error', message: msg });
    } finally {
      setIssueReplyBusyKind(null);
    }
  };

  const handleResolveIssue = async () => {
    const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
    if (!id) return;
    const draft = issueReplyDraft.trim();
    const persisted = (issueReplyRemark || '').trim();
    if (!draft && !persisted) {
      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
      return;
    }
    setIssueReplyRemarkFieldError('');
    setIssueReplyBusyKind('finalize');
    try {
      const body = { submitToCreator: true };
      if (draft) body.replyRemark = draft;
      await api.post(`/action-items/${id}/update`, body);
      setToast({ type: 'success', message: actionItemMessages.issueSubmittedToCreatorSuccess });
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyDraft('');
      setIssueReplyRemarkFieldError('');
      setViewIssueReopenIntent('Closed');
      setViewIssueClosingDate('');
      await refetchActionItems();
      dispatchNotificationsRefresh();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to resolve issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setIssueReplyBusyKind(null);
    }
  };

  /** Issue Review: close without e-sign (e-sign removed for this flow). */
  const executeIssueViewClose = async (id, closeRemark) => {
    if (!id) return;
    if (!viewIssueClosingDate || !/^\d{4}-\d{2}-\d{2}$/.test(viewIssueClosingDate)) {
      setToast({ type: 'error', message: actionItemMessages.closingDateRequired });
      return;
    }
    const { minIso, maxIso } = getActionItemClosingDateBounds(selectedIssueItem?.dateOfIssue ?? selectedIssueItem?.DateOfIssue);
    if (minIso && viewIssueClosingDate < minIso) {
      setToast({ type: 'error', message: actionItemMessages.closingDateBeforeIssue });
      return;
    }
    if (viewIssueClosingDate > maxIso) {
      setToast({ type: 'error', message: actionItemMessages.closingDateFuture });
      return;
    }
    setActionItemClosingId(id);
    try {
      await api.post(`/action-items/${id}/close`, { closedDate: viewIssueClosingDate, remark: closeRemark });
      await refetchActionItems();
      setShowCloseIssueRemarkModal(false);
      closeIssueRemarkRef.current = '';
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyDraft('');
      setIssueReplyRemarkFieldError('');
      setViewIssueReopenIntent('Closed');
      setViewIssueClosingDate('');
      setToast({ type: 'success', message: commonMessages.authSuccess });
      dispatchNotificationsRefresh();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to close issue.';
      setToast({ type: 'error', message: msg });
      throw err;
    } finally {
      setActionItemClosingId(null);
    }
  };

  /** Issue Review: reopen without e-sign (e-sign removed for this flow). */
  const executeIssueViewReopen = async (id) => {
    if (!id) return;
    const modalRemark = (reopenIssueRemarkRef.current || '').trim();
    if (!modalRemark) {
      setToast({ type: 'error', message: 'Remark is required.' });
      return;
    }
    setActionItemReopeningId(id);
    try {
      const reopenBody = { remark: modalRemark };
      await api.post(`/action-items/${id}/reopen`, reopenBody);
      await refetchActionItems();
      setToast({ type: 'success', message: actionItemMessages.issueReopenedSuccess });
      dispatchNotificationsRefresh();
      setIssueWorkflowModalOpen(false);
      setSelectedIssueItem(null);
      setIssueReplyRemark('');
      setIssueReplyDraft('');
      setIssueReplyRemarkFieldError('');
      setViewIssueReopenIntent('Closed');
      setViewIssueClosingDate('');
      setShowReopenIssueRemarkModal(false);
      reopenIssueRemarkRef.current = '';
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to reopen issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemReopeningId(null);
    }
  };

  const fetchVisits = async () => {
    const gen = ++visitsFetchGenerationRef.current;
    try {
      setLoading(true);
      const projectCodeForApi = currentProject == null || currentProject === 'all'
        ? undefined
        : (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode));
      // Always bind visits using stage-wise API to avoid broad fetch.
      const stageForApi = activeStage === 'schedule'
        ? 'Schedule'
        : activeStage === 'onGoing'
          ? 'In Progress'
          : getLabelForStageId(activeStage);
      const response = await visitService.getAll(projectCodeForApi ?? undefined, stageForApi);
      if (gen !== visitsFetchGenerationRef.current) return;
      setVisits(Array.isArray(response) ? response : []);
      setError(null);
    } catch (err) {
      if (gen !== visitsFetchGenerationRef.current) return;
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load visits';
      setError(errorMessage);
      console.error('Error fetching visits:', err);
      setVisits([]);
    } finally {
      if (gen === visitsFetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const refreshVisitsAndStageCounts = () => {
    fetchVisits();
    void refetchStageCounts();
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setSelectedDate(null);
  };

  const handleStageChange = (stage) => {
    setActiveStage(stage);
    setSelectedDate(null);
    setReviewTab('pending'); // Reset tab when changing stage
    // Switch to calendar view for schedule stage, panel view for others. SRM: Second Review opens in Panel by default.
    if (stage === 'schedule') {
      setViewMode('calendar');
    } else {
      setViewMode('panel');
    }
  };

  const handleDateClick = (date) => {
    setVisitBeingEdited(null);
    if (activeStage !== 'schedule' || !canSchedule) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const clickedStart = date ? new Date(date) : null;
    if (clickedStart) clickedStart.setHours(0, 0, 0, 0);
    if (clickedStart && clickedStart < todayStart) return;
    setSelectedDate(date);
    setIsScheduleModalOpen(true);
  };

  const handleScheduleModalClose = () => {
    setIsScheduleModalOpen(false);
    setSelectedDate(null);
    setVisitBeingEdited(null);
  };

  const handleScheduleModalSave = () => {
    setToast({ type: 'success', message: visitMessages.savedSuccess });
    refreshVisitsAndStageCounts();
  };

  const handleCalendarEventClick = (visit) => {
    if (!visit) return;
    setSelectedVisitForActions(visit);
    setIsActionsModalOpen(true);
  };

  const handleModifyFromActions = (visit) => {
    if (!visit || activeStage !== 'schedule' || !canSchedule) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitBeingEdited(visit);
    setSelectedDate(visit.plannedStartDate ? new Date(visit.plannedStartDate) : null);
    setIsScheduleModalOpen(true);
  };

  const handleOpenConfirmPIModal = (visit) => {
    if (!visit) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitForConfirmPI(visit);
    setIsConfirmPIModalOpen(true);
  };

  const handleOpenGenerateLetterModal = (visit) => {
    if (!visit) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitForLetter(visit);
    setIsGenerateLetterModalOpen(true);
  };

  const handleOpenGenerateFollowUpLetterModal = (visit) => {
    if (!visit) return;
    setVisitForFollowUpLetter(visit);
    setIsGenerateFollowUpLetterModalOpen(true);
  };

  const handleOpenExecuteVisitModal = (visit) => {
    if (!visit) return;
    setIsActionsModalOpen(false);
    setSelectedVisitForActions(null);
    setVisitForExecute(visit);
    setIsExecuteVisitModalOpen(true);
  };

  const getVisitId = (v) =>
    v?.scheduledVisitId ?? v?.scheduledVisitID ?? v?.ScheduledVisitID ??
    v?.visitNodeId ?? v?.VisitNodeId ?? v?.visitID ?? v?.VisitID ?? v?.visitId;

  const handleStartReport = async (visit) => {
    const vid = getVisitId(visit);
    if (vid == null) return;
    try {
      const data = await visitService.getAssignedCRFTemplate(vid);
      const templateId = data?.templateId ?? data?.TemplateId ?? data?.templateNo ?? data?.TemplateNo;
      if (templateId) {
        setReportTemplateId(templateId);
        setReportVisit(visit);
        setIsReportModalOpen(true);
      } else {
        setToast({ type: 'error', message: 'No Report template assigned for this visit. Please map a template in Report Management (Map Template to Visit).' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to open report.';
      setToast({ type: 'error', message: msg || 'No Report template assigned for this visit. Please map a template in Report Management (Map Template to Visit).' });
    }
  };

  const handleDraftSaved = (visitId, nCRFHdrNo) => {
    setToast({ type: 'success', message: commonMessages.authSuccess });
    // Only switch "Start Report" to "Continue" when a CRF header was actually saved (entry exists in CRFHdr)
    if (visitId != null && nCRFHdrNo != null && nCRFHdrNo !== '') setVisitsWithCRFDraft(prev => new Set([...prev, Number(visitId)]));
    // Close the CRF Report modal when user clicks Draft Save (success)
    handleCloseReportModal();
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    setReportTemplateId(null);
    setReportVisit(null);
    // Refresh visit list so CRA submit / stage changes are reflected without manual page refresh
    refreshVisitsAndStageCounts();
  };

  /** Open CRF page for review (First Review / Second Review). Reuses same CRF modal as In Progress. */
  const handleReviewCRFPage = async (visit) => {
    const vid = getVisitId(visit);
    if (vid == null) return;
    try {
      const data = await visitService.getAssignedCRFTemplate(vid);
      const templateId = data?.templateId ?? data?.TemplateId ?? data?.templateID ?? data?.templateNo ?? data?.TemplateNo ?? data?.vTemplateNo ?? data?.VTemplateNo;
      if (templateId) {
        setReportTemplateId(templateId);
        setReportVisit(visit);
        setIsReportModalOpen(true);
      } else {
        setToast({ type: 'error', message: 'No Report template assigned for this visit.' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to open CRF for review.';
      setToast({ type: 'error', message: msg });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case VISIT_STATUS.COMPLETED:
        return 'badge-success';
      case VISIT_STATUS.ONGOING:
        return 'badge-info';
      case VISIT_STATUS.CONFIRMED:
        return 'badge-warning';
      case VISIT_STATUS.CANCELLED:
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  // Filter visits by stage
  const getVisitsByStage = (stage) => {
    if (!Array.isArray(visits) || visits.length === 0) {
      return [];
    }

    // Same site/cra filter used for visit list and for action-item visit pool (align with Visit visibility)
    const filtered = visits.filter(v => {
      if (!v) return false;
      if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
      if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
      return true;
    });

    const getStatus = (v) => v.visitStatus ?? v.VisitStatus;
    const getCrfStage = (v) => v.crfStage ?? v.CrfStage ?? '';

    const completedLabel = getLabelForStageId('closed');
    const inProgressLabel = getLabelForStageId('onGoing');

    switch (stage) {
      case 'schedule':
        return filtered.filter(v => {
          const s = getStatus(v);
          return s === VISIT_STATUS.DRAFT || s === VISIT_STATUS.PLANNED || s === VISIT_STATUS.CONFIRMED;
        });
      case 'onGoing': {
        // In Progress tab: CrfStage In Progress / ongoing, OR open DCF released to CRA (matches GetAllVisitsAsync In Progress union).
        return filtered.filter(v => {
          const crf = getCrfStage(v);
          const hasOpenDcfReleasedToCra = v.hasOpenDcfReleasedToCra ?? v.HasOpenDcfReleasedToCra;
          if (hasOpenDcfReleasedToCra) return true;
          if (crf !== '' && crf !== inProgressLabel) return false;
          return getStatus(v) === VISIT_STATUS.ONGOING || (inProgressLabel && crf === inProgressLabel);
        });
      }
      case 'closed':
        return filtered.filter(v => {
          const crf = getCrfStage(v);
          return getStatus(v) === VISIT_STATUS.COMPLETED || (completedLabel && crf === completedLabel);
        });
      default: {
        // Dynamic review stage (id = stageCode string e.g. '301', '311')
        const stageLabel = getLabelForStageId(stage);
        if (!stageLabel) return [];
        return filtered.filter(v => {
          const crf = getCrfStage(v);
          if (completedLabel && crf === completedLabel) return false;
          const hasOpenDCF = v.hasOpenDCF ?? v.HasOpenDCF;
          const openDCFStageRaised = v.openDCFStageRaised ?? v.OpenDCFStageRaised;
          if (hasOpenDCF && openDCFStageRaised === stageLabel) return true;
          return crf === stageLabel;
        });
      }
    }
  };

  const getStageCount = (stage) => {
    return getVisitsByStage(stage).length;
  };

  // Dynamic stages: only 101, 201, 9999 are fixed; between 201 and 9999 use stageCode as id (e.g. 301, 311, 321).
  const stageCodeToId = (code) => {
    if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
    if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
    if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
    if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
    return null;
  };

  const getLabelForStageId = (stageId) => {
    const code = stageId === 'schedule' ? STAGE_WORKFLOW.SCHEDULE : stageId === 'onGoing' ? STAGE_WORKFLOW.IN_PROGRESS : stageId === 'closed' ? STAGE_WORKFLOW.COMPLETED : parseInt(stageId, 10);
    if (Number.isNaN(code)) return '';
    const s = workflowStagesFromApi.find((x) => (x.stageCode ?? x.StageCode) === code);
    return s ? (s.label ?? s.Label ?? s.vDisplayName ?? s.VDisplayName ?? '') : '';
  };

  const isStageWithVisitsAndActions = activeStage === 'onGoing' || (activeStage !== 'schedule' && activeStage !== 'closed');
  const showVisitsTable = (activeStage === 'schedule' || activeStage === 'closed') ||
    (activeStage === 'onGoing' && reviewTab === 'pending') ||
    (isStageWithVisitsAndActions && reviewTab === 'pending');

  const visitsTableFilteredCount = (() => {
    if (!showVisitsTable) return 0;
    const stageVisits = getVisitsByStage(activeStage);
    return stageVisits.filter((v) => visitRowMatchesSearch(v, visitSearch)).length;
  })();

  const showActionItemsTable = reviewTab === 'actions' && (activeStage === 'onGoing' || (activeStage !== 'schedule' && activeStage !== 'closed'));

  const actionItemsTableData = (() => {
    if (!showActionItemsTable) {
      return { paginatedList: [], totalCount: 0, visitById: new Map(), unfilteredIssueCount: 0 };
    }
    const baseFiltered = visits.filter((v) => {
      if (!v) return false;
      if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
      if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
      return true;
    });
    const visitById = new Map();
    baseFiltered.forEach((v) => {
      const id = getVisitId(v);
      if (id != null) {
        const n = Number(id);
        visitById.set(n, v);
        if (String(id) !== String(n)) visitById.set(id, v);
      }
    });
    const getItemVisitId = (item) => item.scheduledVisitID ?? item.ScheduledVisitID;
    const enrichedList = actionItems.map((item) => {
      const vid = getItemVisitId(item);
      const visit = vid != null ? (visitById.get(Number(vid)) ?? visitById.get(vid)) : null;
      return { ...item, visit: visit || null };
    });
    const searchedList = enrichedList.filter((row) =>
      actionItemRowMatchesSearch(row, row.visit, actionItemSearch),
    );
    const totalCount = searchedList.length;
    const unfilteredIssueCount = enrichedList.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / actionItemPageSize));
    const currentPage = Math.min(actionItemPage, totalPages);
    const start = (currentPage - 1) * actionItemPageSize;
    const paginatedList = searchedList.slice(start, start + actionItemPageSize);
    return { paginatedList, totalCount, visitById, unfilteredIssueCount };
  })();

  const actionItemPagerTotalPages = Math.max(
    1,
    Math.ceil(actionItemsTableData.totalCount / actionItemPageSize),
  );
  const actionItemPagerPage = Math.min(actionItemPage, actionItemPagerTotalPages);

  const workflowStages = (() => {
    // Show only stages returned by API (profile-filtered by backend). No fallback to all stages.
    if (!stagesLoaded) return []; // Before first load, show no tabs
    if (workflowStagesFromApi.length === 0) return []; // No stages assigned to profile
    return workflowStagesFromApi
      .filter((s) => (s.stageCode ?? s.StageCode) != null)
      .sort((a, b) => (a.orderNo ?? a.OrderNo ?? 0) - (b.orderNo ?? b.OrderNo ?? 0))
      .map((s) => {
        const code = s.stageCode ?? s.StageCode;
        const id = stageCodeToId(code);
        return {
          id,
          stageCode: code,
          label: s.label ?? s.Label ?? s.vDisplayName ?? s.VDisplayName ?? id,
          count: stageCountsByCode[Number(code)] ?? getStageCount(id),
        };
      });
  })();

  // When stages load: apply ?stage= / ?tab=issues from dashboard (or other deep links), then clamp activeStage to allowed ids
  useEffect(() => {
    if (!stagesLoaded || workflowStagesFromApi.length === 0) return;
    const sorted = [...workflowStagesFromApi]
      .filter((s) => (s.stageCode ?? s.StageCode) != null)
      .sort((a, b) => (a.orderNo ?? a.OrderNo ?? 0) - (b.orderNo ?? b.OrderNo ?? 0));
    const ids = sorted.map((s) => {
      const code = s.stageCode ?? s.StageCode;
      if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
      if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
      if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
      if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
      return null;
    }).filter(Boolean);

    const stageParamRaw = searchParams.get('stage');
    const wantsIssues = searchParams.get('tab') === 'issues';
    const hasNavIntent = (stageParamRaw != null && String(stageParamRaw).trim() !== '') || wantsIssues;

    if (hasNavIntent) {
      let target = parseVisitsUrlStageParam(stageParamRaw);
      if (target && !ids.includes(target)) target = null;
      if (wantsIssues) {
        setReviewTab('actions');
        if (!target) {
          if (ids.includes('onGoing')) target = 'onGoing';
          else target = ids.find((id) => id !== 'schedule' && id !== 'closed') ?? ids[0];
        }
      }
      if (target && ids.includes(target)) {
        setActiveStage(target);
        setViewMode(target === 'schedule' ? 'calendar' : 'panel');
        const profileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
        const targetRow = sorted.find((s) => stageCodeToActiveStageId(s.stageCode ?? s.StageCode) === target);
        const targetLabel = (targetRow?.label ?? targetRow?.Label ?? targetRow?.vDisplayName ?? targetRow?.VDisplayName ?? '').toString().toLowerCase();
        if (profileCode === 'SRM' && targetLabel.includes('second review')) setViewMode('panel');
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('stage');
        next.delete('tab');
        return next;
      }, { replace: true });
      if (target && ids.includes(target)) return;
    }

    if (ids.length > 0 && !ids.includes(activeStage)) {
      setActiveStage(ids[0]);
      // SRM profile: when default stage is Second Review, open Panel View instead of Calendar
      const firstStageLabel = (sorted[0]?.label ?? sorted[0]?.Label ?? sorted[0]?.vDisplayName ?? sorted[0]?.VDisplayName ?? '').toString().toLowerCase();
      const profileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
      if (profileCode === 'SRM' && firstStageLabel.includes('second review')) setViewMode('panel');
    }
  }, [stagesLoaded, workflowStagesFromApi, searchParams, activeStage, user, setSearchParams]);

  // Get action items for selected visit (CRFActionItems from GET /api/action-items?stage=... include scheduledVisitID)
  const getVisitActionItems = (visitId) => {
    if (visitId == null) return [];
    const id = Number(visitId);
    return actionItems.filter(
      (item) => Number(item.scheduledVisitID) === id || item.scheduledVisitID == visitId
    );
  };

  // Render stage-specific table columns
  const renderTableHeaders = () => {
    switch (activeStage) {
      case 'schedule':
        return (
          <tr>
            <th>Visit ID</th>
            <th>Site</th>
            <th>Visit Type</th>
            <th>Scheduled Start Date</th>
            <th>Scheduled End Date</th>
            <th>Edit</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        );
      case 'onGoing':
        if (reviewTab === 'actions') {
          return (
            <tr>
              <th>Reference No</th>
              <th>Visit ID</th>
              <th>Site</th>
              <th>Visit Type</th>
              <th>Field Name</th>
              <th>Issue description</th>
              <th>Assigned To</th>
              <th>Expected Closure Date</th>
              <th>Current Status</th>
              <th>Details</th>
              <th>Actions</th>
            </tr>
          );
        }
        return (
          <tr>
            <th>Visit ID</th>
            <th>Site</th>
            <th>Visit Type</th>
            <th>Visit Start Date</th>
            <th>Visit End Date</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        );
      case 'closed':
        return (
          <tr>
            <th>Visit ID</th>
            <th>Site</th>
            <th>Visit Type</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        );
      default:
        if (isStageWithVisitsAndActions) {
          if (reviewTab === 'pending') {
            return (
              <tr>
                <th>Visit ID</th>
                <th>Site</th>
                <th>Visit Type</th>
                <th>Scheduled Start Date</th>
                <th>Scheduled End Date</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            );
          }
          return (
            <tr>
              <th>Reference No</th>
              <th>Visit ID</th>
              <th>Site</th>
              <th>Visit Type</th>
              <th>Field Name</th>
              <th>Issue description</th>
              <th>Assigned To</th>
              <th>Expected Closure Date</th>
              <th>Current Status</th>
              <th>Details</th>
              <th>Actions</th>
            </tr>
          );
        }
        return null;
    }
  };

  // Render stage-specific table rows
  const renderTableRows = () => {
    const stageVisits = getVisitsByStage(activeStage);
    /** Schedule + Completed have 7 columns; other visit grids (e.g. In Progress) have 6. Must match thead or borders/alignment break. */
    const visitsEmptyRowColSpan = activeStage === 'schedule' || activeStage === 'closed' ? 7 : 6;

    const renderActionItemsGrid = (itemsForStage, visitById) => {
      const getItemVisitId = (item) => item.scheduledVisitID ?? item.ScheduledVisitID;
      const visitActionItems = itemsForStage.map((item) => {
        const vid = getItemVisitId(item);
        const visit = vid != null ? (visitById.get(Number(vid)) ?? visitById.get(vid)) : null;
        return { ...item, visit: visit || null };
      });
      if (visitActionItems.length === 0) {
        const q = (actionItemSearch || '').trim();
        const hadRows = actionItemsTableData.unfilteredIssueCount > 0;
        const emptyMsg = q && hadRows ? 'No issues match your search.' : 'No issues found';
        return (
          <tr>
            <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              {emptyMsg}
            </td>
          </tr>
        );
      }
      return visitActionItems.map((item) => (
          <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber}>
            <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
            <td>{getActionItemVisitIdDisplay(item, item.visit)}</td>
            <td>{getActionItemSiteDisplay(item, item.visit, sites)}</td>
            <td>{getActionItemVisitTypeLabel(item, item.visit)}</td>
            <td>{item.actionItemOn || '-'}</td>
            <td>{item.findingDescription || '-'}</td>
            <td>{item.vAssignTo ?? item.VAssignTo ?? item.assignedToName ?? 'CRA'}</td>
            <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
            <td>
              <span
                className={`badge ${
                  (() => {
                    const st = (item.status || '').toString().trim().toLowerCase();
                    if (st === 'closed') return 'badge-success';
                    return 'badge-warning';
                  })()
                }`}
              >
                {formatActionItemStatusForDisplay(item.status)}
              </span>
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIssueForDetails(item);
                  setIssueDetailsModalOpen(true);
                }}
              >
                Details
              </button>
            </td>
            <td style={{ textAlign: 'center' }}>
              {(() => {
                const status = (item.status || '').toLowerCase();
                const currentProfile = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim().toUpperCase();
                const assignedProfile = (item.assignedToProfileCode ?? '').toString().trim().toUpperCase();
                const canReply = status === 'open' && currentProfile && assignedProfile && currentProfile === assignedProfile;
                const canViewForClose = status === 'resolved' && canShowIssueCloseButton(item);
                const canViewClosedForReopen = status === 'closed' && canShowIssueReopenButton(item);
                if (canReply) {
                  return (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openReplyModal(item);
                      }}
                    >
                      Reply
                    </button>
                  );
                }
                if (canViewForClose || canViewClosedForReopen) {
                  return (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewModal(item);
                      }}
                    >
                      View
                    </button>
                  );
                }
                return <span className="text-muted">-</span>;
              })()}
            </td>
          </tr>
        ));
    };

    if (activeStage === 'onGoing' && reviewTab === 'actions') {
      return renderActionItemsGrid(actionItemsTableData.paginatedList, actionItemsTableData.visitById);
    }

    if (isStageWithVisitsAndActions && activeStage !== 'onGoing' && reviewTab === 'actions') {
      return renderActionItemsGrid(actionItemsTableData.paginatedList, actionItemsTableData.visitById);
    }

    if (stageVisits.length === 0) {
      return (
        <tr className="visit-table-empty-row">
          <td colSpan={visitsEmptyRowColSpan} className="visit-table-empty-cell">
            No visits found in this stage
          </td>
        </tr>
      );
    }

    const q = (visitSearch || '').trim().toLowerCase();
    const filteredVisits = stageVisits.filter((v) => visitRowMatchesSearch(v, visitSearch));
    // Sort by Visit ID (numeric) ascending for grid display
    const sortedVisits = [...filteredVisits].sort((a, b) => {
      const idA = Number(getVisitId(a)) || 0;
      const idB = Number(getVisitId(b)) || 0;
      return idA - idB;
    });
    const totalItems = sortedVisits.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / visitPageSize));
    const currentPage = Math.min(visitPage, totalPages);
    const start = (currentPage - 1) * visitPageSize;
    const paginatedVisits = sortedVisits.slice(start, start + visitPageSize);

    if (paginatedVisits.length === 0) {
      return (
        <tr className="visit-table-empty-row">
          <td colSpan={visitsEmptyRowColSpan} className="visit-table-empty-cell">
            {q ? 'No visits match your search.' : 'No visits found in this stage'}
          </td>
        </tr>
      );
    }

    return paginatedVisits.map((visit) => {
      if (!visit) return null;

      const vid = getVisitId(visit);
      const visitId = formatVisitId(visit);

      switch (activeStage) {
        case 'schedule':
          return (
            <tr
              key={getVisitId(visit)}
            >
              <td>{visitId}</td>
              <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
              <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
              <td>{visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—'}</td>
              <td>{visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—'}</td>
              <td>
                <div className="action-buttons-inline">
                  {canSchedule && (
                    <button
                      type="button"
                      className="action-btn"
                      title="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisitBeingEdited(visit);
                        setSelectedDate(visit.plannedStartDate ? new Date(visit.plannedStartDate) : null);
                        setIsScheduleModalOpen(true);
                      }}
                    >
                      <FiEdit size={18} />
                    </button>
                  )}
                  
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <div className="stage-actions">
                  {canVisitActivity && (
                    <>
                      {(visit.confirmationStatus || visit.ConfirmationStatus) !== 'Confirmed' && !(visit.hasVisitLetter || visit.HasVisitLetter) && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setVisitForLetter(visit); setIsGenerateLetterModalOpen(true); }}>
                          Generate Visit Letter
                        </button>
                      )}
                      {(visit.confirmationStatus || visit.ConfirmationStatus) !== 'Confirmed' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const hasLetter = visit.hasVisitLetter || visit.HasVisitLetter;
                            if (!hasLetter) {
                              setToast({ type: 'info', message: 'Please generate the visit letter first before confirming letter action taken.' });
                              return;
                            }
                            setVisitForConfirmPI(visit);
                            setIsConfirmPIModalOpen(true);
                          }}
                        >
                          Confirm Letter Action Taken
                        </button>
                      )}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const hasLetter = visit.hasVisitLetter || visit.HasVisitLetter;
                          const isConfirmed = (visit.confirmationStatus || visit.ConfirmationStatus) === 'Confirmed';
                          if (!hasLetter) {
                            setToast({ type: 'info', message: 'Please generate the visit letter first before executing the visit.' });
                            return;
                          }
                          if (!isConfirmed) {
                            setToast({ type: 'info', message: 'Please confirm letter action taken first before executing the visit.' });
                            return;
                          }
                          setVisitForExecute(visit);
                          setIsExecuteVisitModalOpen(true);
                        }}
                      >
                        Execute Visit
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          );
        case 'onGoing':
          return (
            <tr
              key={getVisitId(visit)}
            >
              <td>{visitId}</td>
              <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
              <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
              <td>{visit.actualStartDate ? formatDateDDMMMYYYY(visit.actualStartDate) : (visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—')}</td>
              <td>{visit.actualEndDate ? formatDateDDMMMYYYY(visit.actualEndDate) : (visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—')}</td>
              <td style={{ textAlign: 'center' }}>
                {(canStartReport || isViewOnly) && (
                  <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); handleStartReport(visit); }}>
                    {isViewOnly ? 'View' : (visitsWithCRFDraft.has(Number(getVisitId(visit))) ? 'Draft Report' : 'Start Report')}
                  </button>
                )}
              </td>
            </tr>
          );
        case 'closed':
          return (
            <tr
              key={getVisitId(visit)}
            >
              <td>{visitId}</td>
              <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
              <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
              <td>{visit.actualStartDate ? formatDateDDMMMYYYY(visit.actualStartDate) : (visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—')}</td>
              <td>{visit.actualEndDate ? formatDateDDMMMYYYY(visit.actualEndDate) : (visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—')}</td>
              <td>
                <span className={`badge ${getStatusBadgeClass(visit.visitStatus)}`}>
                  {(visit.isFullyCompleted ?? visit.IsFullyCompleted) ? VISIT_STATUS.COMPLETED : (visit.visitStatus || '—')}
                </span>
              </td>
              <td style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {hasCompletedStageRights && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      title="Open approved visit report (read-only)"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartReport(visit);
                      }}
                    >
                      View Report
                    </button>
                  )}
                  {canVisitActivity && (
                    <>
                    {(visit.hasFollowUpLetter ?? visit.HasFollowUpLetter) ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenGenerateFollowUpLetterModal(visit);
                        }}
                      >
                        View
                      </button>
                    ) : !(visit.isFullyCompleted ?? visit.IsFullyCompleted) ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenGenerateFollowUpLetterModal(visit);
                        }}
                      >
                        Follow-Up Letter
                      </button>
                    ) : null}

                    {(visit.hasFollowUpLetter ?? visit.HasFollowUpLetter)
                      && !(visit.isFullyCompleted ?? visit.IsFullyCompleted)
                      && !String(visit.followUpConfirmationStatus ?? visit.FollowUpConfirmationStatus ?? '').trim().toLowerCase().includes('confirmed')
                      && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisitForFollowUpConfirmPI(visit);
                            setIsConfirmFollowUpPIModalOpen(true);
                          }}
                        >
                          Confirm Letter Action Taken
                        </button>
                      )}
                    </>
                  )}
                </div>
              </td>
            </tr>
          );
        default:
          if (isStageWithVisitsAndActions && activeStage !== 'onGoing' && reviewTab === 'pending') {
            return (
              <tr key={getVisitId(visit)}>
                <td>{visitId}</td>
                <td>{visit.vSiteCode ?? visit.siteCode ?? visit.SiteCode ?? '—'}</td>
                <td>{VISIT_TYPES[visit.visitType] || visit.visitType || '—'}</td>
                <td>{visit.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—'}</td>
                <td>{visit.plannedEndDate ? formatDateDDMMMYYYY(visit.plannedEndDate) : '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReviewCRFPage(visit);
                    }}
                  >
                    Review Report
                  </button>
                </td>
              </tr>
            );
          }
          return null;
      }
    });
  };

  if (loading) return <Spinner />;

  return (
    <div className="visit-management">
      {error && <Alert type="error" message={error} />}

      {/* Filters */}
      <div className="visit-filters">
        <div className="filter-group">
          <label className="form-label">Filter by Site</label>
          <select
            className="form-select"
            value={filters.site}
            onChange={(e) => handleFilterChange('site', e.target.value)}
            disabled={loadingFilters}
          >
            <option value="all">All Sites</option>
            {loadingFilters ? (
              <option value="">Loading sites...</option>
            ) : (
              sites.map(site => (
                <option key={site.siteID} value={site.siteCode}>{site.siteDisplay || site.siteCode}</option>
              ))
            )}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">Filter by CRA</label>
          <select
            className="form-select"
            value={filters.cra}
            onChange={(e) => handleFilterChange('cra', e.target.value)}
            disabled={loadingFilters}
          >
            <option value="all">All CRA</option>
            {loadingFilters ? (
              <option value="">Loading CRA...</option>
            ) : cras.length === 0 ? (
              <option value="">No CRA available</option>
            ) : (
              cras.map(cra => (
                <option key={cra.id} value={cra.id}>{cra.name}</option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Workflow Steps – only stages the user has rights to (profile-filtered from API) */}
      {stagesLoaded && workflowStages.length === 0 ? (
        <div className="visit-no-stages" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          <p>No stages assigned to your profile. Contact your administrator to get access to stages.</p>
        </div>
      ) : !stagesLoaded ? (
        <div className="workflow-steps" style={{ padding: '1rem', textAlign: 'center', color: '#888' }}>
          Loading stages…
        </div>
      ) : (
        <>
      <div className="workflow-steps">
        {workflowStages.map((stage) => (
          <div
            key={stage.id}
            className={`step ${activeStage === stage.id ? 'active' : ''}`}
            onClick={() => handleStageChange(stage.id)}
          >
            <button className="step-circle">{stage.count}</button>
            <div className="step-label">{stage.label}</div>
          </div>
        ))}
      </div>

      {/* View Toggle Buttons */}
      <div className="view-toggle">
        <button
          className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('calendar')}
        >
          <FiCalendar /> Calendar View
        </button>
        <button
          className={`btn ${viewMode === 'panel' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('panel')}
        >
          <FiColumns /> Panel View
        </button>
      </div>

      {/* Main Content Area */}
      <div className="visit-content-area">
        {viewMode === 'panel' ? (
          <div className="visit-panel-view">
            {/* Tabs for In Progress, First Review and Second Review */}
            {isStageWithVisitsAndActions && (
              <div className="review-tabs">
                <div
                  className={`review-tab ${reviewTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setReviewTab('pending')}
                >
                  {activeStage === 'onGoing' ? 'In Progress' : (getLabelForStageId(activeStage) || 'Pending Reviews')}
                </div>
                <div
                  className={`review-tab ${reviewTab === 'actions' ? 'active' : ''}`}
                  onClick={() => {
                    setReviewTab('actions');
                    refetchActionItems();
                  }}
                  title="Issues"
                >
                  <FiList size={18} aria-hidden /> Issues
                </div>
              </div>
            )}

            {/* Issues tab: toolbar only when Create Issue is allowed (no duplicate "Issues" title — tab already shows it) */}
            {reviewTab === 'actions' && isStageWithVisitsAndActions && !isViewOnly && (profilePermissions.canRaiseActionItem === 1 || profilePermissions.CanRaiseActionItem === 1) && (
              <div className="review-actions-header">
                <button
                  type="button"
                  className="btn btn-primary btn-icon-only"
                  onClick={() => setIsCreateActionItemModalOpen(true)}
                  title="Create Issue"
                  aria-label="Create Issue"
                >
                  <FiPlus size={20} aria-hidden /> Create Issue
                </button>
              </div>
            )}

            <div className="visit-table-container" style={{ position: 'relative' }}>
              {activeStage === 'onGoing' && reviewTab === 'pending' && loadingCRFDraftStatus && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    zIndex: 10,
                  }}
                  aria-live="polite"
                  aria-busy="true"
                >
                  <Spinner />
                </div>
              )}
              {showVisitsTable && (
                <div className="visit-table-toolbar data-grid-toolbar">
                  <div className="data-grid-controls">
                    <div className="data-grid-search">
                      <input
                        type="text"
                        className="form-input data-grid-search-input"
                        placeholder="Search"
                        value={visitSearch}
                        onChange={(e) => {
                          setVisitSearch(e.target.value);
                          setVisitPage(1);
                        }}
                      />
                    </div>
                    <select
                      value={visitPageSize}
                      onChange={(e) => {
                        setVisitPageSize(Number(e.target.value));
                        setVisitPage(1);
                      }}
                      className="form-select page-size-select data-grid-page-size"
                    >
                    <option value={5}>5 items</option>
                    <option value={10}>10 items</option>
                    <option value={25}>25 items</option>
                    <option value={50}>50 items</option>
                  </select>
                  </div>
                </div>
              )}
              {showActionItemsTable && (
                <div className="visit-table-toolbar data-grid-toolbar">
                  <div className="data-grid-controls">
                    <div className="data-grid-search">
                      <input
                        type="search"
                        className="form-input data-grid-search-input"
                        placeholder="Search"
                        value={actionItemSearch}
                        onChange={(e) => {
                          setActionItemSearch(e.target.value);
                          setActionItemPage(1);
                        }}
                        aria-label="Search issues"
                      />
                    </div>
                    <select
                      value={actionItemPageSize}
                      onChange={(e) => {
                        setActionItemPageSize(Number(e.target.value));
                        setActionItemPage(1);
                      }}
                      className="form-select page-size-select data-grid-page-size"
                    >
                      <option value={5}>5 items</option>
                      <option value={10}>10 items</option>
                      <option value={25}>25 items</option>
                      <option value={50}>50 items</option>
                    </select>
                  </div>
                </div>
              )}
              <table className="visit-table">
                <thead>
                  {renderTableHeaders()}
                </thead>
                <tbody>
                  {renderTableRows()}
                </tbody>
              </table>
              {showVisitsTable && visitsTableFilteredCount > 0 && (
                <div className="visit-table-pagination" style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination
                    page={visitPage}
                    pageSize={visitPageSize}
                    totalItems={visitsTableFilteredCount}
                    onPageChange={setVisitPage}
                    onPageSizeChange={(size) => {
                      setVisitPageSize(size);
                      setVisitPage(1);
                    }}
                    showPageSize={false}
                  />
                </div>
              )}
              {showActionItemsTable && actionItemsTableData.totalCount > 0 && (
                <div className="visit-table-pagination" style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination
                    page={actionItemPagerPage}
                    pageSize={actionItemPageSize}
                    totalItems={actionItemsTableData.totalCount}
                    onPageChange={setActionItemPage}
                    onPageSizeChange={(size) => {
                      setActionItemPageSize(size);
                      setActionItemPage(1);
                    }}
                    showPageSize={false}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="visit-calendar-view">
            <VisitCalendar
              visits={getVisitsByStage(activeStage)}
              activeStage={activeStage}
              filters={filters}
              onEventClick={handleCalendarEventClick}
              onDateClick={handleDateClick}
            />
          </div>
        )}
      </div>

      </>
      )}

      {/* Schedule Visit Modal */}
      <ScheduleVisitModal
        isOpen={isScheduleModalOpen}
        onClose={handleScheduleModalClose}
        selectedDate={selectedDate}
        onSave={handleScheduleModalSave}
        defaultSite={filters.site !== 'all' ? filters.site : null}
        mode={visitBeingEdited ? 'edit' : 'create'}
        existingVisit={visitBeingEdited}
      />

      {/* Report Review Modal */}
      <ReportReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false);
          setSelectedVisitForReview(null);
        }}
        visit={selectedVisitForReview}
        resolvedPiName={getVisitPI(selectedVisitForReview, sites)}
        reportId={selectedVisitForReview?.reportID || null}
      />

      {/* Visit Actions Modal (Calendar event click) */}
      <VisitActionsModal
        isOpen={isActionsModalOpen}
        onClose={() => {
          setIsActionsModalOpen(false);
          setSelectedVisitForActions(null);
        }}
        visit={selectedVisitForActions}
        resolvedPiName={getVisitPI(selectedVisitForActions, sites)}
        onRefresh={refreshVisitsAndStageCounts}
        onModify={handleModifyFromActions}
        onOpenConfirmPI={handleOpenConfirmPIModal}
        onOpenGenerateLetter={handleOpenGenerateLetterModal}
        onOpenExecuteVisit={handleOpenExecuteVisitModal}
        onStartReport={handleStartReport}
        canSchedule={canSchedule}
        canVisitActivity={canVisitActivity}
        hasCompletedStageRights={hasCompletedStageRights}
        canStartReport={canStartReport || isViewOnly}
        isViewOnly={isViewOnly}
        activeStage={activeStage}
      />

      {/* Confirm Letter Action Taken Modal */}
      <ConfirmPIModal
        isOpen={isConfirmPIModalOpen}
        onClose={() => {
          setIsConfirmPIModalOpen(false);
          setVisitForConfirmPI(null);
        }}
        visit={visitForConfirmPI}
        resolvedPiName={getVisitPI(visitForConfirmPI, sites)}
        onConfirmed={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Generate Visit Letter Modal */}
      <GenerateVisitLetterModal
        isOpen={isGenerateLetterModalOpen}
        onClose={() => {
          setIsGenerateLetterModalOpen(false);
          setVisitForLetter(null);
        }}
        visit={visitForLetter}
        resolvedPiName={getVisitPI(visitForLetter, sites)}
        onGenerated={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Generate Follow-Up Letter Modal (Completed stage) */}
      <GenerateFollowUpLetterModal
        isOpen={isGenerateFollowUpLetterModalOpen}
        onClose={() => {
          setIsGenerateFollowUpLetterModalOpen(false);
          setVisitForFollowUpLetter(null);
        }}
        visit={visitForFollowUpLetter}
        resolvedPiName={getVisitPI(visitForFollowUpLetter, sites)}
        mode={(() => {
          const v = visitForFollowUpLetter;
          if (!v) return 'edit';
          const fu = String(v.followUpConfirmationStatus ?? v.FollowUpConfirmationStatus ?? '').trim().toLowerCase();
          if (fu === 'rejected') return 'edit';
          return (v.hasFollowUpLetter ?? v.HasFollowUpLetter) ? 'view' : 'edit';
        })()}
        onGenerated={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Confirm PI Availability (Follow-Up Letter) */}
      <ConfirmFollowUpPIModal
        isOpen={isConfirmFollowUpPIModalOpen}
        onClose={() => {
          setIsConfirmFollowUpPIModalOpen(false);
          setVisitForFollowUpConfirmPI(null);
        }}
        visit={visitForFollowUpConfirmPI}
        resolvedPiName={getVisitPI(visitForFollowUpConfirmPI, sites)}
        onConfirmed={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Execute Visit Modal */}
      <ExecuteVisitModal
        isOpen={isExecuteVisitModalOpen}
        onClose={() => {
          setIsExecuteVisitModalOpen(false);
          setVisitForExecute(null);
        }}
        visit={visitForExecute}
        resolvedPiName={getVisitPI(visitForExecute, sites)}
        cras={cras}
        onExecuted={() => {
          setToast({ type: 'success', message: commonMessages.authSuccess });
          refreshVisitsAndStageCounts();
        }}
      />

      {/* Create Action Item Modal (Open Action Items tab) */}
      <CreateActionItemModal
        isOpen={isCreateActionItemModalOpen}
        onClose={() => setIsCreateActionItemModalOpen(false)}
        onSave={() => refetchActionItems()}
        defaultProject={currentProject || undefined}
        defaultSiteId=""
        stageVisits={isStageWithVisitsAndActions ? getVisitsByStage(activeStage) : []}
        visitIdsWithCRFDraft={Array.from(visitsWithCRFDraft)}
        pi="Dr. Tapan Shah"
      />

      <Modal
        isOpen={issueDetailsModalOpen}
        onClose={() => {
          setIssueDetailsModalOpen(false);
          setSelectedIssueForDetails(null);
        }}
        title="Issue details"
        size="large"
        className="visit-issue-details-modal"
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setIssueDetailsModalOpen(false);
              setSelectedIssueForDetails(null);
            }}
          >
            Close
          </button>
        }
      >
        {selectedIssueForDetails ? (
          <div className="visit-issue-details-wrap">
            <table className="visit-table visit-issue-details-table" style={{ width: '100%', marginTop: 0 }}>
              <thead>
                <tr>
                  <th>Date of Awareness</th>
                  <th>Date of Issue Creation</th>
                  <th>Issue Category</th>
                  <th>Priority</th>
                  <th>Closure Remark</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {formatDateDDMMMYYYY(
                      selectedIssueForDetails.dateOfAwareness ?? selectedIssueForDetails.DateOfAwareness
                    )}
                  </td>
                  <td>
                    {formatDateDDMMMYYYY(
                      selectedIssueForDetails.dateOfIssue ?? selectedIssueForDetails.DateOfIssue
                    )}
                  </td>
                  <td>
                    {(() => {
                      const base = (selectedIssueForDetails.issueCategory ?? selectedIssueForDetails.IssueCategory ?? '')
                        .toString()
                        .trim();
                      const other = (selectedIssueForDetails.issueCategoryOther ?? selectedIssueForDetails.IssueCategoryOther ?? '')
                        .toString()
                        .trim();
                      if (other) return `${base}: ${other}`;
                      return base || '—';
                    })()}
                  </td>
                  <td>
                    {(selectedIssueForDetails.priorityDescription
                      ?? selectedIssueForDetails.PriorityDescription
                      ?? selectedIssueForDetails.severity
                      ?? selectedIssueForDetails.Severity
                      ?? '—')}
                  </td>
                  <td>
                    {(() => {
                      const st = (selectedIssueForDetails.status ?? selectedIssueForDetails.Status ?? '')
                        .toString()
                        .trim()
                        .toLowerCase();
                      if (st !== 'closed') return '—';
                      const closure = (selectedIssueForDetails.closureRemark ?? selectedIssueForDetails.ClosureRemark ?? '')
                        .toString()
                        .trim();
                      if (closure) return closure;
                      const raw = selectedIssueForDetails.replyRemark ?? selectedIssueForDetails.ReplyRemark ?? '';
                      return getClosureRemarkFromReplyRemark(raw) || '—';
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={issueWorkflowModalOpen}
        onClose={() => {
          if (issueReplyBusyKind) return;
          setIssueWorkflowModalOpen(false);
          setSelectedIssueItem(null);
          setIssueReplyRemark('');
          setIssueReplyDraft('');
          setIssueReplyRemarkFieldError('');
          setViewIssueReopenIntent('Closed');
          setViewIssueClosingDate('');
        }}
        title="Issue Review"
        size="large"
        className="create-action-item-modal visit-issue-review-modal"
        footer={
          <>
            {issueWorkflowMode === 'reply' ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  disabled={issueReplyBusyKind !== null}
                  onClick={() => {
                    const draft = issueReplyDraft.trim();
                    if (!draft) {
                      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
                      return;
                    }
                    setIssueReplyRemarkFieldError('');
                    void handleUpdateIssueRemarkOnly();
                  }}
                >
                  Update
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={issueReplyBusyKind !== null}
                  onClick={() => {
                    const draft = issueReplyDraft.trim();
                    const persisted = (issueReplyRemark || '').trim();
                    if (!draft && !persisted) {
                      setIssueReplyRemarkFieldError(actionItemMessages.replyRemarkRequired);
                      return;
                    }
                    setIssueReplyRemarkFieldError('');
                    void handleResolveIssue();
                  }}
                >
                  Save
                </button>
              </>
            ) : canShowIssueReopenButton(selectedIssueItem) || canShowIssueCloseButton(selectedIssueItem) ? (
              <>
                {canShowIssueReopenButton(selectedIssueItem) && viewIssueReopenIntent === 'Open' ? (
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    disabled={actionItemReopeningId === (selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo)}
                    onClick={() => {
                      reopenIssueRemarkRef.current = '';
                      setShowReopenIssueRemarkModal(true);
                    }}
                  >
                    {actionItemReopeningId === (selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo) ? 'Reopening...' : 'Re Open'}
                  </button>
                ) : null}
                {canShowIssueCloseButton(selectedIssueItem) && viewIssueReopenIntent === 'Closed' ? (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={actionItemClosingId === (selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo)}
                    onClick={() => {
                      const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
                      if (!id) return;
                      closeIssueRemarkRef.current = '';
                      setShowCloseIssueRemarkModal(true);
                    }}
                  >
                    {actionItemClosingId === (selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo) ? 'Closing...' : 'Close Issue'}
                  </button>
                ) : null}
              </>
            ) : null}
            <button
              type="button"
              className="btn btn-secondary"
              disabled={issueReplyBusyKind !== null}
              onClick={() => {
                setIssueWorkflowModalOpen(false);
                setSelectedIssueItem(null);
                setIssueReplyRemark('');
                setIssueReplyDraft('');
                setIssueReplyRemarkFieldError('');
                setViewIssueReopenIntent('Closed');
                setViewIssueClosingDate('');
              }}
            >
              Close
            </button>
          </>
        }
      >
        {selectedIssueItem && (() => {
          const item = selectedIssueItem;
          const aid = item.actionItemID ?? item.nCRFActionItemNo;
          const resolvedWorkflow = issueWorkflowMode === 'view' && (canShowIssueReopenButton(item) || canShowIssueCloseButton(item));
          const stItem = (item.status ?? item.Status ?? '').toString().trim().toLowerCase();
          const showClosingDateReadOnly = resolvedWorkflow && viewIssueReopenIntent === 'Closed' && stItem === 'closed';
          const showClosingDateEditable = resolvedWorkflow && viewIssueReopenIntent === 'Closed' && stItem === 'resolved' && canShowIssueCloseButton(item);
          const issueViewControlsLocked = issueReplyBusyKind !== null
            || actionItemReopeningId === aid
            || actionItemClosingId === aid;
          const priorityVal = (item.priorityDescription ?? item.severity ?? item.priority ?? '').toString().trim();
          const issueOn = (item.actionItemOn ?? item.issue ?? '').toString().trim() || '—';
          const isClosedIssue = stItem === 'closed'
            || Boolean(item.closedDate ?? item.ClosedDate);
          const displayIssueReplyRemark = isClosedIssue
            ? stripClosureRemarkFromReplyRemark(issueReplyRemark)
            : issueReplyRemark;
          const descEditable = issueWorkflowMode === 'reply'
            ? issueReplyBusyKind === null
            : (resolvedWorkflow && viewIssueReopenIntent === 'Open' && !issueViewControlsLocked);
          const showPriorityExtra = Boolean(priorityVal && !['Low', 'Medium', 'High', 'Critical'].includes(priorityVal));
          const openIssueFieldAudit = (field) => {
            if (!aid) return;
            setIssueViewAuditActionItemId(aid);
            setIssueViewAuditField(field);
            setIssueViewAuditOpen(true);
          };
          return (
            <div className="create-action-item-form visit-issue-review-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Project</label>
                  <input
                    className="form-input"
                    value={(currentProject || '').toString().trim() || '—'}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Site</label>
                  <input
                    className="form-input"
                    value={(() => {
                      const siteCode = (item.siteCode ?? item.SiteCode ?? '').toString().trim();
                      const siteDesc = (item.siteDescription ?? item.SiteDescription ?? '').toString().trim();
                      return [siteCode, siteDesc].filter(Boolean).join(' - ') || siteCode || siteDesc || '—';
                    })()}
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Visit Type</label>
                  <input
                    className="form-input"
                    value={(() => {
                      const vt = (item.visitType ?? item.VisitType ?? '').toString().trim();
                      return VISIT_TYPES[vt] || vt || '—';
                    })()}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Visit</label>
                  <input
                    className="form-input"
                    value={(item.displayVisitCode ?? item.visitId ?? item.VisitId ?? '').toString().trim() || '—'}
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Reference No</label>
                  <input className="form-input" value={item.actionItemNumber ?? ''} disabled />
                </div>
                <div className="form-group">
                  <label className="form-label">Field Name</label>
                  <div className="form-input visit-issue-readonly-block">{issueOn}</div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Issue Description</label>
                <textarea className="form-input" value={item.findingDescription ?? ''} disabled rows={4} />
              </div>

              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <input
                  className="form-input"
                  value={(
                    item.vAssignTo
                    ?? item.VAssignTo
                    ?? item.assignedToName
                    ?? item.assignedToProfileCode
                    ?? ''
                  ).toString().trim() || '—'}
                  disabled
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date of Awareness</label>
                  <input
                    className="form-input"
                    value={formatDateDDMMMYYYY(item.dateOfAwareness ?? item.DateOfAwareness) || '—'}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Date of Issue Creation</label>
                  <input
                    className="form-input"
                    value={formatDateDDMMMYYYY(item.dateOfIssue ?? item.DateOfIssue) || '—'}
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Expected Closure Date</label>
                  <input
                    className="form-input"
                    value={formatDateDDMMMYYYY(item.dueDate ?? item.DueDate) || '—'}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Issue Category</label>
                  <input
                    className="form-input"
                    value={(() => {
                      const base = (item.issueCategory ?? item.IssueCategory ?? '').toString().trim();
                      const other = (item.issueCategoryOther ?? item.IssueCategoryOther ?? '').toString().trim();
                      if (other) return `${base}: ${other}`;
                      return base || '—';
                    })()}
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-input" value={priorityVal} disabled>
                    <option value="">Select priority</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                    {showPriorityExtra ? <option value={priorityVal}>{priorityVal}</option> : null}
                  </select>
                </div>
                <div className="form-group">
                  <div className="visit-issue-label-row">
                    <label className="form-label mb-0">Status</label>
                    {aid ? (
                      <button
                        type="button"
                        className="visit-issue-audit-icon-btn"
                        title="View Status audit"
                        onClick={() => openIssueFieldAudit('vstatus')}
                      >
                        <FiFileText size={14} />
                      </button>
                    ) : null}
                  </div>
                  <select
                    className={`form-input${resolvedWorkflow && !issueViewControlsLocked ? ' visit-issue-status-editable' : ''}`}
                    value={resolvedWorkflow ? viewIssueReopenIntent : normalizeActionItemStatusSelectValue(item.status)}
                    disabled={!resolvedWorkflow || issueViewControlsLocked}
                    onChange={
                      resolvedWorkflow
                        ? (e) => {
                          const v = e.target.value;
                          setViewIssueReopenIntent(v);
                          if (v === 'Closed') {
                            setIssueReplyRemark(viewIssueReplyOriginalRef.current);
                            setIssueReplyDraft('');
                            const b = getActionItemClosingDateBounds(item.dateOfIssue ?? item.DateOfIssue);
                            const today = todayIsoDateLocal();
                            setViewIssueClosingDate((prev) => {
                              if (prev && /^\d{4}-\d{2}-\d{2}$/.test(prev)) {
                                const c = clampIsoDateToMinMax(prev, b.minIso, b.maxIso);
                                if (c === prev) return prev;
                              }
                              return clampIsoDateToMinMax(today, b.minIso, b.maxIso);
                            });
                          } else {
                            setViewIssueClosingDate('');
                          }
                        }
                        : undefined
                    }
                  >
                    {resolvedWorkflow ? (
                      <>
                        <option value="Closed">Close</option>
                        <option value="Open">Open</option>
                      </>
                    ) : (
                      <>
                        <option value="Open">Open</option>
                        <option value="Resolved">Open</option>
                        <option value="Closed">Close</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
              {showClosingDateReadOnly ? (
                <div className="form-group">
                  <label className="form-label mb-0">Closing Date</label>
                  <input
                    type="text"
                    className="form-input visit-issue-closing-date-readonly"
                    readOnly
                    disabled
                    value={(() => {
                      const iso = apiDateTimeToIsoDateLocal(
                        item.closedDate ?? item.ClosedDate ?? item.dClosedDate ?? item.DClosedDate
                      );
                      return (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? formatIsoDateCalendarDDMMMYYYY(iso) : '') || '—';
                    })()}
                    aria-label="Closing Date"
                  />
                </div>
              ) : null}
              {showClosingDateEditable ? (() => {
                const vBounds = getActionItemClosingDateBounds(item.dateOfIssue ?? item.DateOfIssue);
                return (
                <div className="form-group">
                  <label className="form-label mb-0" htmlFor="visit-issue-closing-date-picker">
                    Closing Date
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="visit-issue-closing-date-picker"
                    type="date"
                    className={`form-input visit-issue-status-editable visit-issue-closing-date-input${issueViewControlsLocked ? ' visit-issue-closing-date-input--locked' : ''}`}
                    value={viewIssueClosingDate}
                    min={vBounds.minIso || undefined}
                    max={vBounds.maxIso}
                    onChange={(e) => setViewIssueClosingDate(clampIsoDateToMinMax(e.target.value, vBounds.minIso, vBounds.maxIso))}
                    disabled={issueViewControlsLocked}
                    aria-label="Closing Date"
                  />
                </div>
                );
              })() : null}
              <div className="form-group">
                <div className="visit-issue-label-row">
                  <label className="form-label mb-0">
                    Description of Action
                    {issueWorkflowMode === 'reply' ? <span className="required-asterisk">*</span> : null}
                  </label>
                  {aid ? (
                    <button
                      type="button"
                      className="visit-issue-audit-icon-btn"
                      title="View Description of Action audit"
                      onClick={() => openIssueFieldAudit('vreplyremark')}
                    >
                      <FiFileText size={14} />
                    </button>
                  ) : null}
                </div>
                <ActionItemReplyThread
                  persistedRemark={displayIssueReplyRemark}
                  historyEntries={issueReplyHistoryEntries}
                  draft={issueReplyDraft}
                  onDraftChange={(v) => {
                    setIssueReplyDraft(v);
                    setIssueReplyRemarkFieldError('');
                  }}
                  readOnly={!descEditable}
                  user={user}
                  fieldError={issueWorkflowMode === 'reply' ? issueReplyRemarkFieldError : ''}
                  showRequiredStar={
                    issueWorkflowMode === 'reply' && !(issueReplyRemark || '').toString().trim()
                  }
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      {issueViewAuditOpen && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 3500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}>
          <Modal
            isOpen={true}
            onClose={() => {
              setIssueViewAuditOpen(false);
              setIssueViewAuditActionItemId(null);
              setIssueViewAuditField('');
              setIssueViewAuditTrail([]);
              setIssueViewAuditError(null);
            }}
            title="Audit Details"
            size="fullscreen"
            className="visit-issue-field-audit-modal"
            footer={(
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setIssueViewAuditOpen(false);
                  setIssueViewAuditActionItemId(null);
                  setIssueViewAuditField('');
                  setIssueViewAuditTrail([]);
                  setIssueViewAuditError(null);
                }}
              >
                Close
              </button>
            )}
          >
            {issueViewAuditLoading ? <Spinner /> : null}
            {!issueViewAuditLoading && issueViewAuditError ? (
              <div className="alert alert-danger mb-0">{issueViewAuditError}</div>
            ) : null}
            {!issueViewAuditLoading && !issueViewAuditError && issueViewAuditTrail.length === 0 ? (
              <p className="text-muted mb-0">No audit entries found for this field.</p>
            ) : null}
            {!issueViewAuditLoading && !issueViewAuditError && issueViewAuditTrail.length > 0 ? (() => {
              const fieldLower = (issueViewAuditField || '').toLowerCase();
              const answerFieldName = fieldLower === 'vstatus' ? 'vstatus' : 'vreplyremark';
              const selectedFieldName = fieldLower === 'vstatus' ? 'Status' : 'Description of Action';
              const filteredRows = issueViewAuditTrail.flatMap((hdr, hi) => {
                const details = hdr.details || hdr.Details || [];
                const recordedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
                const sign = hdr.performedBy || hdr.vRecordedSign || '—';
                const offset = hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset || '—';
                const answerDetails = details.filter((d) => (d.vFieldName || d.VFieldName || '').toLowerCase() === answerFieldName);
                if (answerDetails.length === 0) return [];
                return answerDetails.map((dtl, di) => {
                  const rawOld = dtl.vOldValue ?? dtl.VOldValue;
                  const rawNew = dtl.vNewValue ?? dtl.VNewValue;
                  const oldFmt = fieldLower === 'vstatus'
                    ? formatIssueAuditValueForDisplay(rawOld)
                    : formatReplyRemarkPlainForAuditDisplay(rawOld);
                  const newFmt = fieldLower === 'vstatus'
                    ? formatIssueAuditValueForDisplay(rawNew)
                    : formatReplyRemarkPlainForAuditDisplay(rawNew);
                  const mapCell = (fmt) => {
                    if (fmt == null || String(fmt).trim() === '') return '—';
                    const s = String(fmt);
                    if (s === '—') return '—';
                    return fieldLower === 'vstatus' ? formatActionItemStatusForDisplay(s) : s;
                  };
                  return {
                    key: `${hdr.nAuditHdrNo ?? hi}-${di}`,
                    fieldName: selectedFieldName,
                    oldV: mapCell(oldFmt),
                    newV: mapCell(newFmt),
                    recordedOn,
                    sign,
                    offset,
                  };
                });
              });
              return (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered visit-issue-audit-table">
                    <colgroup>
                      <col className="visit-issue-audit-col-field-name" />
                      <col className="visit-issue-audit-col-old" />
                      <col className="visit-issue-audit-col-new" />
                      <col className="visit-issue-audit-col-by" />
                      <col className="visit-issue-audit-col-utc" />
                      <col className="visit-issue-audit-col-offset" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Field Name</th>
                        <th>Old Value</th>
                        <th>New Value</th>
                        <th>Performed By</th>
                        <th>Performed on UTC</th>
                        <th>Performed At Offset</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 ? (
                        <tr><td colSpan={6} className="text-muted">No audit entries for this field.</td></tr>
                      ) : (
                        filteredRows.map((row) => (
                          <tr key={row.key}>
                            <td className="visit-issue-audit-cell-value">{row.fieldName}</td>
                            <td className="visit-issue-audit-cell-value">{row.oldV}</td>
                            <td className="visit-issue-audit-cell-value">{row.newV}</td>
                            <td className="visit-issue-audit-cell-performed-by">{row.sign}</td>
                            <td className="visit-issue-audit-cell-meta visit-issue-audit-cell-utc">{formatPerformedOnUtcToLocal(row.recordedOn, row.offset)}</td>
                            <td className="visit-issue-audit-cell-meta visit-issue-audit-cell-offset">{formatOffsetForDisplay(row.offset)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })() : null}
          </Modal>
        </div>,
        document.body
      )}
      {showCloseIssueRemarkModal && createPortal(
        <ElectronicSign
          show={showCloseIssueRemarkModal}
          onClose={() => {
            if (actionItemClosingId) return;
            setShowCloseIssueRemarkModal(false);
            closeIssueRemarkRef.current = '';
          }}
          title="Enter Remark"
          primaryActionLabel="Save"
          remarkOnlyMode={true}
          showConfirmText={false}
          onAuthSuccess={(vRemarks) => { closeIssueRemarkRef.current = (vRemarks || '').trim(); }}
          onSave={async () => {
            const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
            if (!id) return;
            await executeIssueViewClose(id, closeIssueRemarkRef.current);
          }}
        />,
        document.body
      )}
      {showReopenIssueRemarkModal && createPortal(
        <ElectronicSign
          show={showReopenIssueRemarkModal}
          onClose={() => {
            if (actionItemReopeningId) return;
            setShowReopenIssueRemarkModal(false);
            reopenIssueRemarkRef.current = '';
          }}
          title="Enter Remark"
          primaryActionLabel="Save"
          remarkOnlyMode={true}
          showConfirmText={false}
          onAuthSuccess={(vRemarks) => { reopenIssueRemarkRef.current = (vRemarks || '').trim(); }}
          onSave={async () => {
            const id = selectedIssueItem?.actionItemID ?? selectedIssueItem?.nCRFActionItemNo;
            if (!id) return;
            await executeIssueViewReopen(id);
          }}
        />,
        document.body
      )}

      {/* CRF Report Modal - rendered in portal so it appears above sidebar (avoids z-index stacking) */}
      {isReportModalOpen && reportTemplateId && createPortal(
        <Modal
          isOpen={true}
          onClose={handleCloseReportModal}
          title="Visit Report"
          size="fullscreen"
          className="crf-report-modal"
          showCloseButton={true}
        >
          <CRFForm
            key={`${reportTemplateId}-${getVisitId(reportVisit)}`}
            templateId={reportTemplateId}
            visitId={getVisitId(reportVisit)}
            visitType={reportVisit?.visitType ?? reportVisit?.VisitType ?? 'SSV'}
            visitUiStageLabel={
              isStageWithVisitsAndActions && activeStage !== 'onGoing'
                ? (getLabelForStageId(activeStage) || '').trim()
                : ''
            }
            reviewerStageLabels={crfReviewerStageLabels}
            onClose={handleCloseReportModal}
            onDraftSaved={handleDraftSaved}
            onSubmitSuccess={() => setToast({ type: 'success', message: commonMessages.authSuccess })}
            onApproveSendbackSuccess={() => setToast({ type: 'success', message: commonMessages.authSuccess })}
            onDcfSaveSuccess={() => setToast({ type: 'success', message: commonMessages.authSuccess })}
            embedInModal={true}
            stageVisits={isStageWithVisitsAndActions ? getVisitsByStage(activeStage) : []}
            defaultProject={currentProject || null}
            defaultSiteId={
              reportVisit?.siteID ?? reportVisit?.siteId ??
              (() => {
                const name = reportVisit?.siteName ?? reportVisit?.SiteName ?? reportVisit?.siteCode;
                if (!name) return '';
                const s = sites.find((s) => (s.siteCode ?? (s.siteName || s.sitedescription)) === name);
                return s ? (s.siteID ?? s.siteId ?? '') : '';
              })()
            }
            defaultSiteName={reportVisit?.siteName ?? reportVisit?.SiteName ?? reportVisit?.siteCode ?? ''}
            onActionItemCreated={refetchActionItems}
            readOnly={
              isViewOnly ||
              activeStage === 'closed' ||
              (
                // Dual visibility (SRM-style peek): approve user on a review tab while the visit is still In Progress / CRA-released open DCF — read-only.
                // PM / first-tier (CanRaiseDCF, non-SRM) must still edit and Submit; CRA never hits approve branch.
                canApproveReject &&
                !visitReportDualVisibilityPeekReadOnlyExempt &&
                isStageWithVisitsAndActions && activeStage !== 'onGoing' &&
                (
                  (reportVisit?.crfStage === 'In Progress' || reportVisit?.CrfStage === 'In Progress') ||
                  (reportVisit?.hasOpenDcfReleasedToCra === true || reportVisit?.HasOpenDcfReleasedToCra === true)
                )
              )
            }
            reviewMode={isStageWithVisitsAndActions && activeStage !== 'onGoing'}
          />
        </Modal>,
        document.body
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </div>
  );
};

export default VisitManagement;
